// The admin calendar's drag to move and drag the edges to resize (X36). Pure
// maths and words, no DOM: where a block lands on the row grid, what the
// target reads as, whether it is allowed from the availability data the page
// already holds (the API is the authority), and what the keyboard means. The
// page owns the pointer events; this is what it asks.

import {gridRowAt, gridTime, line24, parseRefusal, periodInZone} from './bookings.js';
import {apiErrorMessage} from './adminActionCopy.js';

/** How many grid rows one slot of a group takes (a 60-minute slot on a 30-minute grid = 2). */
export function stepRows(slotMinutes, rowMinutes) {
  if (!slotMinutes || !rowMinutes) return 1;
  return Math.max(1, Math.round(slotMinutes / rowMinutes));
}

/** Rows snapped to the nearest multiple of the step (0.5 rounds away from zero, so a half-slot pull still moves). */
export function snapRows(rows, step) {
  const s = Math.max(1, step || 1);
  const n = rows / s;
  return (n < 0 ? -Math.round(-n) : Math.round(n)) * s;
}

/**
 * Where a drag puts the block: `mode` is 'move', 'start' (the top edge) or 'end'
 * (the bottom edge); `deltaRows` is the pointer's travel in rows (fractional).
 * The result is snapped to the target group's step and kept inside the grid,
 * with at least one slot left. `anchorRow` keeps a moved block aligned to the
 * target group's slot starts (its first slot's row) rather than the grid's row 0.
 */
export function dragGeometry({mode, block, deltaRows, step, totalRows, anchorRow = 0}) {
  const s = Math.max(1, step || 1);
  const delta = snapRows(deltaRows || 0, s);
  let row = block.row;
  let span = block.span;
  if (mode === 'move') {
    row = block.row + delta;
    // Align to the target group's slot starts.
    row = anchorRow + snapRows(row - anchorRow, s);
    span = Math.max(s, snapRows(block.span, s));
  } else if (mode === 'start') {
    const newRow = anchorRow + snapRows(block.row + delta - anchorRow, s);
    const end = block.row + block.span;
    row = Math.min(newRow, end - s);
    span = end - row;
  } else if (mode === 'end') {
    span = Math.max(s, snapRows(block.span + delta, s));
  }
  if (row < 0) {
    if (mode === 'start') span += row;
    row = 0;
  }
  if (totalRows != null) {
    // X44 (R20 Low 3): a block pushed past the grid's end lands on the group's last slot start that fits, never
    // between slots ("Pick a slot start" at 21:10 on a 30-minute group); a resize clamps to whole slots too.
    if (mode === 'move' && row + span > totalRows) row = Math.max(0, anchorRow + Math.floor((totalRows - span - anchorRow) / s) * s);
    if (mode === 'move' && row < anchorRow) row = Math.max(0, anchorRow);
    if (row + span > totalRows) span = Math.max(s, Math.floor((totalRows - row) / s) * s);
  }
  return {row, span};
}

/**
 * X44 (R20 H1): the booking in hand, for fading its block: a pointer drag of a
 * block once it has moved, else a keyboard change. A drag to create holds no
 * booking, so it reads null rather than throwing in render.
 */
export function heldBookingId(drag, kb) {
  if (drag?.active && drag.moved && drag.booking?.id) return drag.booking.id;
  return kb?.bookingId ?? null;
}

/** The same moment, however it is written ("…T18:00:00.000Z" and "…T18:00:00.0000000Z" are one instant). */
export function sameInstant(a, b) {
  if (!a || !b) return false;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  return !Number.isNaN(ta) && ta === tb;
}

/**
 * X44 (R20 H2): the slot start among `values` (the API's own strings) that names
 * the instant `iso` does, so a select built from the API's slots finds the one
 * the calendar meant; null when none does.
 */
export function slotStartMatching(values, iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (values ?? []).find(v => new Date(v).getTime() === t) ?? null;
}

/**
 * X44 (R20 Low 4): who is told after the change, as one sentence: "The match's
 * players are told of the new place." A move to another playing area at the same
 * time says place, a change of time on the same area says time, both say both.
 */
export function notifiedLine(preview) {
  const names = (preview?.notified ?? []).filter(Boolean);
  if (!names.length) return null;
  const subject = names.join(', ');
  const plural = names.length > 1 || /players$/.test(names[0]);
  const what = preview.areaChanged && !preview.timeChanged ? 'place' : preview.timeChanged && !preview.areaChanged ? 'time' : 'time and place';
  const line = `${subject} ${plural ? 'are' : 'is'} told of the new ${what}.`;
  return line.charAt(0).toUpperCase() + line.slice(1);
}

/** The instants a row and span name on the grid (rows are the grid's rows, `rowMinutes` apart). */
export function rowsToTimes(rows, row, span, rowMinutes) {
  const first = rows?.[row];
  if (!first) return {startsAt: null, endsAt: null};
  const start = first.time ?? new Date(first.startsAt).getTime();
  // X94 (R35 M1): rows are the API's slots and need not be the same length: the end is the next row's start, or the
  // last row's own end; rows without one (older callers) are rowMinutes apart.
  const after = rows[row + span];
  const lastRow = rows[row + span - 1];
  const end = after ? (after.time ?? new Date(after.startsAt).getTime())
    : lastRow?.end != null && row + span === rows.length ? lastRow.end
      : start + span * rowMinutes * 60000;
  return {startsAt: new Date(start).toISOString(), endsAt: new Date(end).toISOString()};
}

/**
 * Each area's cells by grid row, from the day's slots (the painter's rows are
 * the smallest slot): `rowStates.get(areaId)[row]` = {status, bookingId, kind,
 * slotRow, slotSpan} or undefined outside the group's booking hours.
 */
export function rowStates(day, areas, groups, grid) {
  const out = new Map();
  if (!grid?.rows?.length) return out;
  // X94 (R35 M1): the grid's own rows (the API's slots), looked up, never start + n × step.
  const startOf = (iso) => gridRowAt(grid.rows, iso);
  const endOf = (iso) => gridRowAt(grid.rows, iso, {end: true});
  const groupSlots = new Map((day?.groups ?? []).map(g => [g.groupId, g.slots ?? []]));
  for (const area of areas ?? []) {
    const states = new Array(grid.rows.length);
    for (const s of groupSlots.get(area.groupId) ?? []) {
      const cell = (s.areas ?? []).find(c => c.areaId === area.id);
      if (!cell) continue;
      const row = startOf(s.startsAt);
      const span = Math.max(1, endOf(s.endsAt) - row);
      for (let r = row; r < row + span && r < states.length; r++) {
        if (r < 0) continue;
        states[r] = {status: cell.status, bookingId: cell.bookingId ?? null, kind: s.kind ?? null, slotRow: row, slotSpan: span, openPlay: !!s.openPlay};
      }
    }
    // X78 (R27 N6): an open-play change has no slots, so its rows had no state and a drag there read "outside hours".
    // Each uncovered row of the change is an open-play state (any row a start), chipped "open play".
    const group = (day?.groups ?? []).find(g => g.groupId === area.groupId);
    for (const o of (group?.overrides ?? []).filter(x => x.kind === 'OpenPlay' && x.startsAt && x.endsAt)) {
      const from = Math.max(0, startOf(o.startsAt));
      const to = Math.min(states.length, endOf(o.endsAt));
      for (let r = from; r < to; r++) if (!states[r]) states[r] = {status: 'open', bookingId: null, kind: null, slotRow: r, slotSpan: 1, openPlay: true};
    }
    // X94 (R35 H2): a gap between the day's slots is the rules' own open play, drawn as such; its rows say so too.
    const own = groupSlots.get(area.groupId) ?? [];
    if (own.length) {
      const from = startOf(own.reduce((m, x) => (x.startsAt < m ? x.startsAt : m), own[0].startsAt));
      const to = Math.min(states.length, endOf(own.reduce((m, x) => (x.endsAt > m ? x.endsAt : m), own[0].endsAt)));
      for (let r = from; r < to; r++) if (!states[r]) states[r] = {status: 'open', bookingId: null, kind: null, slotRow: r, slotSpan: 1, openPlay: true, weekly: true};
    }
    out.set(area.id, states);
  }
  return out;
}

export const BLOCKING = new Set(['booked', 'mine', 'match', 'frozen', 'closed']);
/** The cells a booking can never land on, whoever is dragging: another booking (X72: the one hard stop). */
export const TAKEN = new Set(['booked', 'mine', 'match']);

/**
 * X72 (Grant 2026-10-20): on the admin calendar every rule is a chip, never a
 * stop, except a clash. `admin` turns a reason into a chip (amber ghost) and
 * carries on checking; without it the reason is a stop (red ghost), as for a
 * member's view. Returns true when the check stopped.
 */
function bend(out, admin, chip, reason) {
  if (!admin) { out.reason = reason; return true; }
  if (!out.chips.includes(chip)) out.chips.push(chip);
  return false;
}

/**
 * Client-side validity of a target (the API re-checks everything): closures,
 * clashes, open play, outside the hours, past times, slot alignment, and the
 * rules a match booking has. Matches-only time and a walk-up area are allowed
 * for admins with a chip, as the Book dialog allows them. X72: with `admin`
 * every other rule is a chip too (outside hours, closed, frozen, in the past,
 * off the step, crossing two kinds of hours); a clash stays a stop, and a
 * match booking's time still follows its match.
 */
export function targetValidity({states, area, row, span, step, booking, mode, now = Date.now(), rows, rowMinutes, admin = false}) {
  if (!area) return {ok: false, reason: 'No playing area there'};
  const isMatch = booking?.kind === 'Match' || booking?.kind === 'MeetHold';
  const out = {chips: [], reason: null};
  const col = states?.get(area.id) ?? [];
  const first = col[row];
  if (!first) { if (bend(out, admin, 'outside hours', 'Outside booking hours')) return {ok: false, reason: out.reason}; }
  else if (first.slotRow !== row && bend(out, admin, 'not a slot start', 'Pick a slot start')) return {ok: false, reason: out.reason};
  for (let r = row; r < row + span; r++) {
    const c = col[r];
    if (!c) { if (bend(out, admin, 'outside hours', 'Outside booking hours')) return {ok: false, reason: out.reason}; continue; }
    if (TAKEN.has(c.status) && c.bookingId !== booking?.id) return {ok: false, reason: 'Taken'};
    if (c.bookingId && c.bookingId !== booking?.id) return {ok: false, reason: 'Taken'};
    if (c.openPlay) { if (bend(out, admin, 'open play', 'Open play then: nothing to book')) return {ok: false, reason: out.reason}; continue; }
    if (c.kind && first?.kind && c.kind !== first.kind && !admin) return {ok: false, reason: 'Crosses two kinds of hours'};
    if (c.status === 'closed' && bend(out, admin, 'closed time', 'Closed then')) return {ok: false, reason: out.reason};
    if (c.status === 'frozen' && bend(out, admin, 'frozen', 'Frozen then')) return {ok: false, reason: out.reason};
    if (c.status === 'open' && !isMatch && bend(out, admin, 'walk-up area', 'Open play then: nothing to book')) return {ok: false, reason: out.reason};
    if (c.status === 'matchesOnly' && !out.chips.includes('matches only')) out.chips.push('matches only');
  }
  const last = col[row + span - 1];
  if (last && last.slotRow + last.slotSpan !== row + span && bend(out, admin, 'not a slot end', 'Pick a slot end')) return {ok: false, reason: out.reason};
  if (!area.canBeBooked && !isMatch && !out.chips.includes('walk-up area')) out.chips.push('walk-up area');
  const times = rows && rowMinutes ? rowsToTimes(rows, row, span, rowMinutes) : null;
  const started = booking && new Date(booking.startsAt).getTime() <= now;
  if (started && mode === 'end') {
    // X40: a booking in progress keeps its start; its end may move, never to before now (X72: a chip for an admin).
    if (times?.endsAt && new Date(times.endsAt).getTime() <= now && bend(out, admin, 'in the past', 'Ends before now')) return {ok: false, reason: out.reason};
  } else if (started) { if (bend(out, admin, 'in the past', 'Already started')) return {ok: false, reason: out.reason}; }
  else if (times?.startsAt && new Date(times.startsAt).getTime() <= now && bend(out, admin, 'in the past', 'Already started')) return {ok: false, reason: out.reason};
  if (isMatch) {
    // The time is the match's (B7/BE5): only the area may change (a hard rule for admins too).
    const sameTime = times?.startsAt && booking && new Date(times.startsAt).getTime() === new Date(booking.startsAt).getTime() && new Date(times.endsAt).getTime() === new Date(booking.endsAt).getTime();
    if (mode !== 'move' || !sameTime) return {ok: false, reason: 'Change the time from the match'};
  }
  if (booking?.sportId && area.sportIds?.length && !area.sportIds.includes(booking.sportId)) return {ok: false, reason: `${area.name} isn't for that sport`};
  return {ok: true, chips: out.chips};
}

/**
 * X40: what the admin can do with a block, and why not when they can't, so a
 * block is never silently dead: `move` (drag it elsewhere), `resize` ('both'
 * edges, 'end' only for a booking in progress, or 'none'), `hint` (the words
 * on a block that can't be moved; null when it can).
 */
export function blockAbility(booking, {now = Date.now(), timeZone = null} = {}) {
  const none = (hint) => ({move: false, resize: 'none', hint});
  if (!booking) return none(null);
  if (!['Queued', 'Held', 'PendingPayment', 'Confirmed'].includes(booking.status)) {
    return none(booking.status === 'NoShow' ? 'No-show' : booking.status === 'Cancelled' || booking.status === 'CancelledByClub' ? 'Cancelled' : booking.status === 'Expired' ? 'Expired' : 'Can\'t be changed');
  }
  const start = new Date(booking.startsAt).getTime();
  const end = new Date(booking.endsAt).getTime();
  if (end <= now) return none('Ended');
  const isMatch = booking.kind === 'Match' || booking.kind === 'MeetHold';
  if (start <= now) {
    if (isMatch) return none(`Started at ${gridTime(booking.startsAt, timeZone)}. Its time follows the ${booking.kind === 'Match' ? 'match' : 'meet'}.`);
    return {move: false, resize: 'end', hint: `Started at ${gridTime(booking.startsAt, timeZone)}. Drag the bottom edge to change when it ends.`};
  }
  if (isMatch) return {move: true, resize: 'none', hint: `Its time follows the ${booking.kind === 'Match' ? 'match' : 'meet'}. Drag it to another playing area to move it.`};
  return {move: true, resize: 'both', hint: null};
}

/**
 * "Court 2 · 18:30–19:30" — the live label on the ghost. X91 (Grant 2026-10-21:
 * every time in the grid in 24-hour time): the grid clock (periodInZone), as the
 * time column says it.
 */
export function ghostLabel(areaName, startsAt, endsAt, timeZone) {
  if (!startsAt || !endsAt) return areaName ?? '';
  return `${areaName} · ${periodInZone(startsAt, endsAt, timeZone)}`;
}

/** The block has not moved: a click, not a drag (the detail dialog opens). */
export function isClick(dx, dy, threshold = 4) {
  return Math.abs(dx) < threshold && Math.abs(dy) < threshold;
}

/**
 * Whether a drop needs the confirm dialog before anything is saved: money on
 * the booking, a match (its players are told), guests or a visitor who are
 * told, or any resize of a paid booking. A plain free move just saves, with Undo.
 * The API's preview can also ask (NeedsConfirmation).
 */
export function needsConfirm(booking, {mode = 'move', preview = null, chips = null} = {}) {
  if (preview?.needsConfirmation) return true;
  // X72: a change outside the rules (the grid's chips) is confirmed after the drop too; the dialog lists the overrides.
  if (chips?.length) return true;
  if (!booking) return false;
  if (booking.kind === 'Match' || booking.kind === 'MeetHold') return true;
  if (booking.kind === 'Visitor') return true;
  if ((booking.participants ?? []).length > 0) return true;
  const paid = booking.paid || (booking.totalMinor ?? 0) > 0 || (booking.extraDueMinor ?? 0) > 0;
  if (paid) return true;
  return mode !== 'move' && paid;
}

/**
 * X84 (R30 F1): a change the grid saw as plain is saved at once, but the API is the judge: it weighs the booking's
 * subject's own allowance (X81), which the grid can't see, so a booking made FOR a member who may not book there is
 * answered CONFIRM_NEEDED. That answer is not an error: the caller opens the same confirm dialog (from the preview,
 * which lists the API's reasons). Returns {outcome: 'moved', moved} | {outcome: 'ask', reason} | {outcome: 'error', error}.
 */
export async function tryPlainMove(move) {
  try {
    return {outcome: 'moved', moved: await move()};
  } catch (e) {
    const r = parseRefusal(apiErrorMessage(e, ''));
    return r.kind === 'confirm' ? {outcome: 'ask', reason: r.message} : {outcome: 'error', error: e};
  }
}

/**
 * What a key means on a focused block: Alt+Arrows move (up/down a slot, left/
 * right an area), Alt+Shift+Up/Down shorten/lengthen from the end, Enter
 * confirms a pending keyboard change, Escape cancels. Null = not ours.
 */
export function keyIntent(e) {
  if (!e) return null;
  if (e.key === 'Escape') return {kind: 'cancel'};
  if (e.key === 'Enter') return {kind: 'confirm'};
  if (!e.altKey) return null;
  const resize = e.shiftKey;
  switch (e.key) {
    case 'ArrowUp': return resize ? {kind: 'resize', dRows: -1} : {kind: 'move', dRows: -1, dCols: 0};
    case 'ArrowDown': return resize ? {kind: 'resize', dRows: 1} : {kind: 'move', dRows: 1, dCols: 0};
    case 'ArrowLeft': return resize ? null : {kind: 'move', dRows: 0, dCols: -1};
    case 'ArrowRight': return resize ? null : {kind: 'move', dRows: 0, dCols: 1};
    default: return null;
  }
}

/** Autoscroll near the edges of the scroll box: pixels per frame, growing towards the edge. */
export function autoscrollDelta(x, y, rect, edge = 40, max = 14) {
  const ramp = (d) => d >= edge ? 0 : Math.ceil(((edge - d) / edge) * max);
  let dx = 0;
  let dy = 0;
  if (x - rect.left < edge) dx = -ramp(x - rect.left);
  else if (rect.right - x < edge) dx = ramp(rect.right - x);
  if (y - rect.top < edge) dy = -ramp(y - rect.top);
  else if (rect.bottom - y < edge) dy = ramp(rect.bottom - y);
  return {dx, dy};
}

/** The move request body for a target (EndsAt only when the length changed, so older APIs read it as a plain move). */
export function moveBody(booking, {areaId, startsAt, endsAt}, {confirm = false, refund = null, payLink = false} = {}) {
  const sameLength = booking && new Date(endsAt).getTime() - new Date(startsAt).getTime() === new Date(booking.endsAt).getTime() - new Date(booking.startsAt).getTime();
  const body = {playingAreaId: areaId, startsAt, version: booking?.version ?? null};
  if (!sameLength) body.endsAt = endsAt;
  if (confirm) body.confirm = true;
  if (refund !== null) body.refundDifference = refund;
  if (payLink) body.sendPayLink = true;
  return body;
}

/**
 * X85 (R31 N3): the toast after a move, plain or confirmed in the dialog: "Moved to …" with Undo. A confirmed move
 * (an override of the rules) keeps Undo when no money is involved: putting it back is the same override in reverse.
 * When the change moved money or could (a refund of the difference, a pay link, any price difference), Undo is left
 * off: a move back would not take the refund back or withdraw the link, so the admin moves it by hand instead.
 * Returns {text, undo}.
 */
export function movedToast(moved, preview = null, timeZone = null) {
  // X94 (R35 L2): the API's line on the grid's 24-hour clock ("Moved to Fri 23 Oct · 11:00–12:00 · Court 2.").
  const line = moved?.line ? line24(moved.line, moved.startsAt, moved.endsAt, timeZone) : null;
  const text = moved?.message ? (moved.line ? moved.message.replace(moved.line, line) : moved.message) : (line ? `Moved to ${line}.` : 'Done.');
  const money = !!preview && (!!preview.canRefund || !!preview.canSendPayLink || (preview.refundMinor ?? 0) > 0 || (preview.extraMinor ?? 0) > 0);
  return {text, undo: !money};
}

/** Where a block was before a change, for Undo and for snapping back. */
export function placeOf(booking) {
  return booking ? {areaId: booking.playingAreaId, startsAt: booking.startsAt, endsAt: booking.endsAt} : null;
}

// ── X40: drag across empty time to book it ──

/**
 * The range a drag to create covers: from the slot the press started in to the
 * slot the pointer is in now (up or down), whole slots of the group's step,
 * on the group's slot starts (`anchorRow`) and inside the grid. Rows are grid
 * rows; a fractional pointer row is the row it is in. A click (no travel) is
 * the one slot pressed.
 */
export function createRange({originRow, pointerRow, step, totalRows, anchorRow = 0}) {
  const s = Math.max(1, step || 1);
  const lastRow = totalRows != null ? totalRows - 1 : Infinity;
  const clamp = (r) => Math.max(0, Math.min(lastRow, Math.floor(r ?? 0)));
  const slotOf = (r) => Math.max(anchorRow, anchorRow + Math.floor((clamp(r) - anchorRow) / s) * s);
  const a = slotOf(originRow);
  const b = slotOf(pointerRow ?? originRow);
  const row = Math.min(a, b);
  let end = Math.max(a, b) + s;
  // The last slot may not fit the grid (a shorter final band): drop it rather than draw a part slot.
  if (totalRows != null) while (end > totalRows && end - s > row) end -= s;
  return {row, span: Math.max(1, end - row)};
}

/** Whole slots in a span of rows. */
export function slotsOf(span, step) {
  return Math.max(1, Math.round((span || 0) / Math.max(1, step || 1)));
}

/** "1 h 30", "2 h", "45 min": the length on the create ghost. */
export function durationWords(minutes) {
  const m = Math.max(0, Math.round(minutes || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (!h) return `${r} min`;
  return r ? `${h} h ${String(r).padStart(2, '0')}` : `${h} h`;
}

/**
 * "18:00–19:30 · 1 h 30": the live label while dragging across empty time, on the grid's 24-hour
 * clock (X91, periodInZone). X46 (R21 N8): no area name; the ghost sits in that area's own column, whose
 * header names it, and with it the label was cut off before the duration, the number the ghost
 * exists for, in a 176px column.
 */
export function createLabel(startsAt, endsAt, timeZone) {
  if (!startsAt || !endsAt) return '';
  return `${periodInZone(startsAt, endsAt, timeZone)} · ${durationWords((new Date(endsAt).getTime() - new Date(startsAt).getTime()) / 60000)}`;
}

/**
 * The longest booking anyone may make on a group, in minutes, from the
 * availability's audiences (null = no limit: an audience books all day, or
 * nobody but the club books here).
 */
export function longestMinutes(group) {
  const list = (group?.audiences ?? []).filter(a => a.canBook);
  if (!list.length) return null;
  if (list.some(a => a.allDay || a.maxDurationMinutes == null)) return null;
  return Math.max(...list.map(a => a.maxDurationMinutes));
}

export const CREATABLE = new Set(['free', 'notYetOpen', 'matchesOnly', 'open']);

/**
 * Client-side validity of a range to book (the API re-checks everything on the
 * quote and the create): the hours, slot starts and ends, closed, frozen,
 * taken, past, the window and the longest booking anyone may make. Matches-only
 * time and a walk-up area are allowed with a chip, as the Book dialog allows
 * them (the admin picks who it is for in the dialog, so the booker's own rules
 * are the quote's to say).
 */
export function createValidity({states, area, row, span, step, now = Date.now(), rows, rowMinutes, group, admin = false}) {
  if (!area) return {ok: false, reason: 'No playing area there'};
  const out = {chips: [], reason: null};
  const col = states?.get(area.id) ?? [];
  const first = col[row];
  if (!first) { if (bend(out, admin, 'outside hours', 'Outside booking hours')) return {ok: false, reason: out.reason}; }
  else if (first.slotRow !== row && bend(out, admin, 'not a slot start', 'Pick a slot start')) return {ok: false, reason: out.reason};
  for (let r = row; r < row + span; r++) {
    const c = col[r];
    if (!c) { if (bend(out, admin, 'outside hours', 'Outside booking hours')) return {ok: false, reason: out.reason}; continue; }
    // X72: a clash is the one stop for everyone.
    if (c.bookingId || TAKEN.has(c.status)) return {ok: false, reason: 'Taken'};
    // X78 (R27 N6): open-play time (a change, or a booking's made-up slot's neighbours) is its own chip.
    if (c.openPlay) { if (bend(out, admin, 'open play', 'Open play then: nothing to book')) return {ok: false, reason: out.reason}; continue; }
    if (c.kind && first?.kind && c.kind !== first.kind && !admin) return {ok: false, reason: 'Crosses two kinds of hours'};
    if (c.status === 'closed' && bend(out, admin, 'closed time', 'Closed then')) return {ok: false, reason: out.reason};
    if (c.status === 'frozen' && bend(out, admin, 'frozen', 'Frozen then')) return {ok: false, reason: out.reason};
    if (c.status === 'notYetOpen' && bend(out, admin, 'beyond the window', 'Before the window opens')) return {ok: false, reason: out.reason};
    if (c.status === 'matchesOnly' && !out.chips.includes('matches only')) out.chips.push('matches only');
  }
  const last = col[row + span - 1];
  if (last && last.slotRow + last.slotSpan !== row + span && bend(out, admin, 'not a slot end', 'Pick a slot end')) return {ok: false, reason: out.reason};
  if (!area.canBeBooked && !out.chips.includes('walk-up area')) out.chips.push('walk-up area');
  const times = rows && rowMinutes ? rowsToTimes(rows, row, span, rowMinutes) : null;
  // X94 (R35 L5): a range that has started but not ended is "already started", not "in the past".
  const startT = times?.startsAt ? new Date(times.startsAt).getTime() : NaN;
  const endT = times?.endsAt ? new Date(times.endsAt).getTime() : NaN;
  if (startT <= now && bend(out, admin, endT > now ? 'already started' : 'in the past', 'Already started')) return {ok: false, reason: out.reason};
  const longest = longestMinutes(group);
  // X72 (Grant: "it doesn't let me drag longer than the 2 hour max set"): the limit stops a member's drag; an admin's extends with the chip.
  const minutes = times?.startsAt && times?.endsAt ? (new Date(times.endsAt).getTime() - new Date(times.startsAt).getTime()) / 60000 : span * (rowMinutes || 0);
  if (longest != null && rowMinutes && minutes > longest && bend(out, admin, `longer than allowed (${durationWords(longest)})`, `Longer than the longest booking (${durationWords(longest)})`)) return {ok: false, reason: out.reason};
  void step;
  return {ok: true, chips: out.chips};
}

/**
 * What a key means on a focused empty cell: Shift+Down/Up stretch the range a
 * slot down or up from the cell, Enter opens the booking dialog for it, Escape
 * drops it. Null = not ours (Alt is the blocks' modifier).
 */
export function createKeyIntent(e) {
  if (!e) return null;
  if (e.key === 'Escape') return {kind: 'cancel'};
  if (e.key === 'Enter') return {kind: 'confirm'};
  if (!e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return null;
  if (e.key === 'ArrowDown') return {kind: 'extend', dir: 1};
  if (e.key === 'ArrowUp') return {kind: 'extend', dir: -1};
  return null;
}

/**
 * X59: the edges autoscroll watches when the card scrolls sideways only and
 * the PAGE scrolls up and down (Grant 2026-10-19: the grid grows to its full
 * height). Left and right are the card's own. Up: only while rows are hidden
 * under the pinned header (the card's top is above the header's bottom), and
 * then the edge is that bottom. Down: only while the card runs past the
 * viewport, and then the edge is the viewport's bottom. An edge that is not
 * there is infinitely far, so the pointer is never near it.
 */
/**
 * Grant 2026-11-02 ("when there are more courts than fit and the grid scrolls horizontally, it isn't obvious there are
 * more columns off-screen"): whether a sideways scroller has more to scroll each way, from its scrollLeft, clientWidth
 * and scrollWidth (an element can be passed as is). The grid shows a fade on that edge and an arrow button while there
 * is more that way; when everything fits both are false. A 1px tolerance for fractional widths.
 */
export function scrollCues({scrollLeft = 0, clientWidth = 0, scrollWidth = 0} = {}, tolerance = 1) {
  const max = Math.max(0, (Number(scrollWidth) || 0) - (Number(clientWidth) || 0));
  if (max <= tolerance) return {left: false, right: false};
  const x = Math.max(0, Number(scrollLeft) || 0);
  return {left: x > tolerance, right: x < max - tolerance};
}

export function pageScrollEdges(rect, headBottom, viewportHeight) {
  return {
    left: rect.left,
    right: rect.right,
    top: rect.top < headBottom ? headBottom : -Infinity,
    bottom: rect.bottom > viewportHeight ? viewportHeight : Infinity,
  };
}
